// G7 — the client-facing re-authorisation flow, one component for every place a Flow C
// re-auth can start: `ConsultRoomFrame.jsx` (a patient watching their own room after the
// scheduled lane's T-48h off-session authorisation failed) and `ClientPortal.jsx`'s
// consult row (the same failure, seen from the client area). Both used to hard-code
// `consent: {accepted: true, text_key: "consult.disclosure.v1"}` straight off a button
// click, with no disclosure ever shown and — worse — no `pendingConsult` stash written
// before the redirect, so a re-auth round trip to Stripe and back landed on
// `CheckoutSuccess.jsx`'s "missing context" branch instead of the room.
//
// This is now the ONE place a re-authorisation happens: the visitor must actively REVIEW
// `ConsultCeilingDisclosure` (same component, same consent checkbox as the original
// booking) before anything is sent, the stash is written BEFORE the intent call — same
// ordering as `ConsultBooking.jsx`'s original path — and `consent.text_key` is read off
// the quote's own `disclosureKeys`, never a literal string: a hardcoded key can drift from
// whatever the server actually disclosed, and CONSENT_REQUIRED would be the honest result
// of that drift, not a cosmetic one.
import React, { useState } from "react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
// Relative: gateway/ has no "@/" Jest shim — resolved for real, same convention as every
// other gateway-consuming component in this build.
import defaultGatewayApi from "../gateway/api";
import { checkoutReturnFields } from "../lib/tenantPath";
import { useConsultIntent } from "../gateway/hooks/useConsultIntent";
import { useConsultQuote } from "../gateway/hooks/useConsultQuote";
import { gatewayErrorText } from "../gateway/errorText";
import { writePendingConsult } from "../lib/pendingConsult";
import ConsultCeilingDisclosure from "./ConsultCeilingDisclosure";

/**
 * @param {string} appointmentId
 * @param {string} token the patient's `manage_token` capability
 * @param {string} cancelPath tenant-relative path Stripe returns to on Cancel
 * @param {import("../gateway/contract").ConsultQuote} [quote] already-fetched quote
 *   (`ConsultRoomFrame` already holds one via `useConsultQuote`); omitted, this panel
 *   fetches its own (`ClientPortal`'s row has none).
 * @param {object} [gatewayApi] DI seam (contract §9)
 */
export default function ConsultReauthPanel({
  appointmentId, token, cancelPath, quote: suppliedQuote, gatewayApi = defaultGatewayApi,
}) {
  const t = useT("public");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [reviewing, setReviewing] = useState(false);

  const { data: fetchedQuote } = useConsultQuote(suppliedQuote ? null : appointmentId, token, gatewayApi);
  const quote = suppliedQuote || fetchedQuote;
  const disclosureKeys = quote?.disclosureKeys || [];
  const hasDisclosure = disclosureKeys.length > 0;

  const { creating, error, createIntent } = useConsultIntent(appointmentId, gatewayApi);

  const handleAccept = async () => {
    // Stashed BEFORE the redirect fires: the ONLY way `/merci/consultation` can recover
    // the appointment/token pair once Stripe sends the browser back (lib/pendingConsult.js).
    writePendingConsult({ appointmentId, token });
    await createIntent({
      token,
      origin_url: window.location.origin,
      // Tenant-aware, like every other checkout in the product: in `/t/{slug}/` path
      // mode a bare "/merci/consultation" resolves to the PLATFORM apex, which serves the
      // SaaS marketing page (BUG-039), not this practice's site.
      ...checkoutReturnFields("/merci/consultation", cancelPath),
      locale: lang,
      consent: { accepted: true, text_key: disclosureKeys[0] },
    });
  };

  if (!reviewing) {
    return (
      <div className="flex flex-col items-end gap-1.5" data-testid="consult-reauth-panel">
        <div className="inline-flex items-center gap-1.5 text-xs text-rose-600">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{t("consult.reauth.explain")}</span>
        </div>
        <Button size="sm" onClick={() => setReviewing(true)} data-testid="consult-reauth-review">
          {t("consult.reauth.review")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="consult-reauth-panel">
      {quote && !hasDisclosure ? (
        // Present but empty: the server has priced this consult yet has no disclosure
        // text registered for it — a defect, not a normal "still loading" state, so it
        // gets its own explanation rather than a spinner that never resolves. The button
        // is genuinely disabled (not merely inert): nothing here can ever call the intent.
        <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-3" data-testid="consult-reauth-no-disclosure">
          <p className="text-sm text-eh-muted">{t("consult.reauth.noDisclosure")}</p>
          <Button disabled className="w-full" data-testid="consult-reauth-continue-disabled">
            {t("consult.disclosure.continue")}
          </Button>
        </div>
      ) : (
        <ConsultCeilingDisclosure quote={quote} onAccept={handleAccept} />
      )}
      {error && (
        <p className="text-xs text-rose-600" role="alert">{gatewayErrorText(error.code, tc)}</p>
      )}
      {creating && (
        <p className="text-xs text-eh-muted flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("consult.booking.redirecting")}
        </p>
      )}
    </div>
  );
}
